import { useEffect, useState } from "react";
import LoadingOverlay from "react-loading-overlay";
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Col,
  FormGroup,
  Label,
  Row,
  Spinner,
  CardTitle,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import EnterpriseService from "../../common/services/enterprise-service";
import Select from "react-select";
import reactSelectCustomStyles from "../../common/plugins/reactSelectStyles";
import Paginator from "../ui/Paginator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import truckyService from "../../common/services/trucky-service";
import CustomBadge from "../ui/CustomBadge";
import moment from "moment";
import { Link } from "react-router-dom";
import BoxDynamicAd from "../../ads/BoxDynamicAd";
import LazyImage from "../../web/components/common/LazyImage";
import TimeZoneData from "../TimeZoneData";

const CompanyLeaderboards = ({ container, mode }) => {
  const es = new EnterpriseService();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [searchParameters, setSearchParameters] = useState(
    truckyService.loadSearchParameters(`company-leaderboards-filters-${mode}`, {
      country_code: "",
      game: "1",
      stats_type: "real_miles",
    }),
  );
  const [countries, setCountries] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, perPage: 18 });
  const [totalPages, setTotalPages] = useState(0);
  const [currentPeriod, setCurrentPeriod] = useState({
    month: moment.utc().month(),
    year: moment.utc().year(),
  });

  const isDistance = mode === "distance";
  const pageTitle = isDistance
    ? "Companies Distance Leaderboards"
    : "Companies Hardcore Leaderboards";

  useEffect(() => {
    truckyService.saveSearchParameters(
      `company-leaderboards-filters-${mode}`,
      searchParameters,
    );
  }, [searchParameters]);

  useEffect(() => {
    truckyService.setDocumentTitle(pageTitle);
    getCountries();
  }, [pageTitle]);

  useEffect(() => {
    getCompanies();
  }, [
    pagination,
    currentPeriod,
    searchParameters.game,
    searchParameters.stats_type,
  ]);

  const getCountries = async () => {
    setCountries(await es.getCountries());
  };

  const getCompanies = async () => {
    setLoading(true);

    const params = {
      ...searchParameters,
      month: currentPeriod.month + 1,
      year: currentPeriod.year,
    };

    const result = isDistance
      ? await es.getCompaniesLeaderboards(params, pagination)
      : await es.getCompaniesHardcoreLeaderboards(params, pagination);

    if (result != null) {
      setCompanies(result.data);
      setTotalPages(result.last_page);
    }

    setLoading(false);
  };

  const search = () => {
    setPagination({ ...pagination, page: 1 });
  };

  const getCompanyLogo = (company) => {
    return (
      <LazyImage
        src={company.avatar_url}
        className="directory-company-logo mb-3 rounded"
        placeholder="/assets/company-placeholder.png"
      />
    );
  };

  const getPositionAccentClass = (position) => {
    if (position === 1) return "leaderboard-rank-top1";
    if (position === 2) return "leaderboard-rank-top2";
    if (position === 3) return "leaderboard-rank-top3";
    return "";
  };

  const handlePageClick = (event) => {
    setPagination({ ...pagination, page: event.selected + 1 });
  };

  const nextPeriod = () => {
    const newDate = moment
      .utc()
      .year(currentPeriod.year)
      .month(currentPeriod.month)
      .add(1, "month");

    setCurrentPeriod({ month: newDate.month(), year: newDate.year() });
  };

  const prevPeriod = () => {
    const newDate = moment
      .utc()
      .year(currentPeriod.year)
      .month(currentPeriod.month)
      .subtract(1, "month");

    setCurrentPeriod({ month: newDate.month(), year: newDate.year() });
  };

  const isPrevDisabled = () => false;

  const isNextDisabled = () => {
    const newDate = moment
      .utc()
      .year(currentPeriod.year)
      .month(currentPeriod.month)
      .add(1, "month");
    const endDate = moment.utc().add(1, "month");

    return (
      newDate.month() === endDate.month() && newDate.year() === endDate.year()
    );
  };

  const getPeriodDescription = () => {
    return (
      <>
        {container.localeManager
          .moment()
          .utc()
          .year(currentPeriod.year)
          .month(currentPeriod.month)
          .format("MMMM")}{" "}
        {currentPeriod.year}
      </>
    );
  };

  const handleRowsPerPageChange = (perPage) => {
    setPagination({ ...pagination, page: 1, perPage });
  };

  const getLeaderboardsTypeSelected = () => {
    switch (searchParameters.stats_type) {
      case "total":
        return container.localeManager.strings.total;
        break;
      case "road_trip_real_miles":
        return container.localeManager.strings.road_trip_real_miles;
        break;
      case "road_trip_race_miles":
        return container.localeManager.strings.road_trip_race_miles;
        break;
      case "transport_system":
        return container.localeManager.strings.public_transport;
        break;
      case "real_miles":
        return container.localeManager.strings.realMiles;
        break;
      case "race_miles":
        return container.localeManager.strings.raceMiles;
        break;
      case "wotr":
        return "World of Trucks";
        break;
      case "truckersmp":
        return "TruckersMP";
        break;
    }
  };

  return (
    <div>
      <div className="page-hero">
        <span className="page-hero-icon">
          <FontAwesomeIcon icon="database" className="mx-0"></FontAwesomeIcon>
        </span>
        <div>
          <h1 className="page-hero-title">{pageTitle}</h1>
        </div>
      </div>
      <LoadingOverlay
        active={loading}
        spinner={
          <Spinner
            color="primary"
            style={{ width: "4rem", height: "4rem" }}
          ></Spinner>
        }
      >
        <Row noGutters id="enterpriseContainer">
          <Col>
            <Card className="h-100">
              <CardBody>
                <>
                  <div className="filters-toolbar mb-3">
                    {isDistance && (
                      <div className="d-flex items-center justify-content-center mb-3 gap-3 flex-wrap">
                        <ButtonGroup>
                          <Button
                            color={
                              searchParameters.game == "1"
                                ? "success"
                                : "primary"
                            }
                            onClick={() =>
                              setSearchParameters({
                                ...searchParameters,
                                game: "1",
                                stats_type: "total",
                              })
                            }
                            active={searchParameters.game == "1"}
                            size="sm"
                          >
                            Euro Truck Simulator 2
                          </Button>
                          <Button
                            color={
                              searchParameters.game == "2"
                                ? "success"
                                : "primary"
                            }
                            onClick={() =>
                              setSearchParameters({
                                ...searchParameters,
                                game: "2",
                                stats_type: "total",
                              })
                            }
                            active={searchParameters.game == "2"}
                            size="sm"
                          >
                            American Truck Simulator
                          </Button>
                        </ButtonGroup>
                        <UncontrolledDropdown>
                          <DropdownToggle caret>
                            {getLeaderboardsTypeSelected()}
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem header>
                              {container.localeManager.strings.truckMode}
                            </DropdownItem>
                            <DropdownItem
                              color={
                                searchParameters.stats_type == "total"
                                  ? "success"
                                  : "primary"
                              }
                              onClick={() =>
                                setSearchParameters({
                                  ...searchParameters,
                                  stats_type: "total",
                                })
                              }
                              active={searchParameters.stats_type == "total"}
                              size="sm"
                            >
                              {container.localeManager.strings.total}
                            </DropdownItem>
                            <DropdownItem
                              color={
                                searchParameters.stats_type == "real_miles"
                                  ? "success"
                                  : "primary"
                              }
                              onClick={() =>
                                setSearchParameters({
                                  ...searchParameters,
                                  stats_type: "real_miles",
                                })
                              }
                              active={
                                searchParameters.stats_type == "real_miles"
                              }
                              size="sm"
                            >
                              {container.localeManager.strings.realMiles}
                            </DropdownItem>
                            <DropdownItem
                              color={
                                searchParameters.stats_type == "race_miles"
                                  ? "success"
                                  : "primary"
                              }
                              onClick={() =>
                                setSearchParameters({
                                  ...searchParameters,
                                  stats_type: "race_miles",
                                })
                              }
                              active={
                                searchParameters.stats_type == "race_miles"
                              }
                              size="sm"
                            >
                              {container.localeManager.strings.raceMiles}
                            </DropdownItem>
                            <DropdownItem
                              color={
                                searchParameters.stats_type == "wotr"
                                  ? "success"
                                  : "primary"
                              }
                              onClick={() =>
                                setSearchParameters({
                                  ...searchParameters,
                                  stats_type: "wotr",
                                })
                              }
                              active={searchParameters.stats_type == "wotr"}
                              size="sm"
                            >
                              WOTR
                            </DropdownItem>
                            <DropdownItem
                              color={
                                searchParameters.stats_type == "truckersmp"
                                  ? "success"
                                  : "primary"
                              }
                              onClick={() =>
                                setSearchParameters({
                                  ...searchParameters,
                                  stats_type: "truckersmp",
                                })
                              }
                              active={
                                searchParameters.stats_type == "truckersmp"
                              }
                              size="sm"
                            >
                              TruckersMP
                            </DropdownItem>
                            <DropdownItem header>
                              {container.localeManager.strings.coaches}
                            </DropdownItem>
                            <DropdownItem
                              color={
                                searchParameters.stats_type ==
                                "transport_system"
                                  ? "success"
                                  : "primary"
                              }
                              onClick={() =>
                                setSearchParameters({
                                  ...searchParameters,
                                  stats_type: "transport_system",
                                })
                              }
                              active={
                                searchParameters.stats_type ==
                                "transport_system"
                              }
                              size="sm"
                            >
                              {container.localeManager.strings.public_transport}
                            </DropdownItem>
                            {searchParameters.game == "2" && (
                              <>
                                <DropdownItem header>
                                  {container.localeManager.strings.dispatch_job}
                                </DropdownItem>
                                <DropdownItem
                                  color={
                                    searchParameters.stats_type ==
                                    "road_trip_real_miles"
                                      ? "success"
                                      : "primary"
                                  }
                                  onClick={() =>
                                    setSearchParameters({
                                      ...searchParameters,
                                      stats_type: "road_trip_real_miles",
                                    })
                                  }
                                  active={
                                    searchParameters.stats_type ==
                                    "road_trip_real_miles"
                                  }
                                  size="sm"
                                >
                                  {
                                    container.localeManager.strings
                                      .road_trip_real_miles
                                  }
                                </DropdownItem>
                                <DropdownItem
                                  color={
                                    searchParameters.stats_type ==
                                    "road_trip_race_miles"
                                      ? "success"
                                      : "primary"
                                  }
                                  onClick={() =>
                                    setSearchParameters({
                                      ...searchParameters,
                                      stats_type: "road_trip_race_miles",
                                    })
                                  }
                                  active={
                                    searchParameters.stats_type ==
                                    "road_trip_race_miles"
                                  }
                                  size="sm"
                                >
                                  {
                                    container.localeManager.strings
                                      .road_trip_race_miles
                                  }
                                </DropdownItem>
                              </>
                            )}
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </div>
                    )}
                    {!isDistance && (
                      <div className="d-flex items-center justify-content-center mb-3 gap-3 flex-wrap">
                        <ButtonGroup>
                          <Button color="success" active={true} size="sm">
                            {container.localeManager.strings.points}
                          </Button>
                        </ButtonGroup>
                        <ButtonGroup>
                          <Button
                            color={
                              searchParameters.game == "1"
                                ? "success"
                                : "primary"
                            }
                            onClick={() =>
                              setSearchParameters({
                                ...searchParameters,
                                game: "1",
                              })
                            }
                            active={searchParameters.game == "1"}
                            size="sm"
                          >
                            Euro Truck Simulator 2
                          </Button>
                          <Button
                            color={
                              searchParameters.game == "2"
                                ? "success"
                                : "primary"
                            }
                            onClick={() =>
                              setSearchParameters({
                                ...searchParameters,
                                game: "2",
                              })
                            }
                            active={searchParameters.game == "2"}
                            size="sm"
                          >
                            American Truck Simulator
                          </Button>
                        </ButtonGroup>
                      </div>
                    )}
                    <FormGroup row>
                      <Col sm={4}>
                        <Label>{container.localeManager.strings.country}</Label>
                        <Select
                          onChange={(val) =>
                            setSearchParameters({
                              ...searchParameters,
                              country_code: val == null ? "" : val.code,
                            })
                          }
                          value={countries.find(
                            (m) => m.code == searchParameters.country_code,
                          )}
                          options={countries}
                          styles={reactSelectCustomStyles}
                          getOptionLabel={(m) => m.name}
                          getOptionValue={(m) => m.code}
                          isClearable={true}
                          backspaceRemovesValue={true}
                        ></Select>
                      </Col>
                      <Col className="d-flex align-items-end justify-content-end mt-3 mt-md-none">
                        <Button size="sm" onClick={search} color="primary">
                          {container.localeManager.strings.search}
                        </Button>
                      </Col>
                    </FormGroup>
                  </div>
                  {!loading && (
                    <>
                      <div className="my-3">
                        <Paginator
                          rowsPerPageSelected={pagination.perPage}
                          rowsPerPage={[18, 36, 54, 72, 90]}
                          handleRowsPerPageChange={handleRowsPerPageChange}
                          showRowsPerPage={true}
                          forcePage={pagination.page - 1}
                          totalPages={totalPages}
                          handlePageClick={handlePageClick}
                        >
                          <>
                            <Button
                              disabled={isPrevDisabled()}
                              className="btn-icon w-30px h-30px me-3"
                              color="active-light-primary"
                              onClick={() => prevPeriod()}
                              size="sm"
                            >
                              <FontAwesomeIcon
                                className="me-0"
                                icon="chevron-left"
                              ></FontAwesomeIcon>
                            </Button>
                            {getPeriodDescription()}
                            <Button
                              disabled={isNextDisabled()}
                              className="btn-icon w-30px h-30px ms-3"
                              color="active-light-primary"
                              onClick={() => nextPeriod()}
                              size="sm"
                            >
                              <FontAwesomeIcon
                                className="me-0"
                                icon="chevron-right"
                              ></FontAwesomeIcon>
                            </Button>
                          </>
                        </Paginator>
                      </div>
                      <Row className="directory-company-grid g-4 pb-3">
                        {companies.map((m) => {
                          const accentClass = getPositionAccentClass(
                            m.position,
                          );
                          return (
                            <Col key={m.id} xs={12} md={6} xl={4}>
                              <Card
                                className={`directory-company-card leaderboard-company-card h-100 ${accentClass}`}
                              >
                                <CardBody className="text-center d-flex flex-column">
                                  <Link
                                    to={`/company/${m.id}`}
                                    className="directory-company-link d-flex flex-column h-100"
                                  >
                                    <CustomBadge
                                      className="company-leaderboard-position-badge"
                                      color="primary"
                                    >
                                      {m.position}
                                    </CustomBadge>
                                    {getCompanyLogo(m)}
                                    <CardTitle className="text-uppercase font-weight-bold mb-1">
                                      <h4 className="company-name mb-0">
                                        {m.name}
                                      </h4>
                                    </CardTitle>
                                    <div className="company-badge-row mt-auto">
                                      {m.flag_url != null && (
                                        <img
                                          src={m.flag_url}
                                          className="company-flag"
                                          alt="flag"
                                        />
                                      )}
                                      <span className="leaderboard-company-value">
                                        {isDistance &&
                                          `${container.localeManager.numeral(m.driven_distance).format("0,0")} ${m.distance_unit}`}
                                        {!isDistance &&
                                          `${container.localeManager.numeral(m.points).format("0,0")} HP`}
                                      </span>
                                    </div>
                                  </Link>
                                </CardBody>
                              </Card>
                            </Col>
                          );
                        })}
                      </Row>
                      <div className="mt-4">
                        <Paginator
                          rowsPerPageSelected={pagination.perPage}
                          rowsPerPage={[18, 36, 54, 72, 90]}
                          handleRowsPerPageChange={handleRowsPerPageChange}
                          forcePage={pagination.page - 1}
                          totalPages={totalPages}
                          handlePageClick={handlePageClick}
                        ></Paginator>
                      </div>
                      <TimeZoneData container={container}></TimeZoneData>
                    </>
                  )}
                  {!loading && companies.length == 0 && (
                    <div className="notice d-flex bg-light-danger rounded border-danger border border-dashed p-6 mt-5">
                      {container.localeManager.strings.noCompanies}
                    </div>
                  )}
                </>
              </CardBody>
            </Card>
          </Col>
          {!container.dataService.data.enablePremiumFeature() && (
            <aside className="ad-column-placeholder">
              {!window.overwolf && (
                <BoxDynamicAd container={container} className="my-3" />
              )}
            </aside>
          )}
        </Row>
      </LoadingOverlay>
    </div>
  );
};

export default CompanyLeaderboards;
